// The parts of Books, each its own page with its own link (#/books/banking): Riverstone's side list, and
// Blueprint's Accounting rail in the prototype's groups (pages/ux-full app.js: Money in, Money out, Books).
// Books setup is set once, beside the company's other setup: its rail entry is under Setup (setup audit).
// Who opens a part is Books' own check, by permission: Payroll is the owner's and the bookkeeper's (QA-274).
import { seesPayroll } from './model.ts';
import type { BooksPart } from './props.ts';

export type BooksGroup = 'Money in' | 'Money out' | 'Books' | 'Setup';

export interface BooksPartDef {
  part: BooksPart;
  /** Riverstone's side list. */
  label: string;
  /** The screen's title, and `bp` its title in Blueprint, where it's the prototype's question. */
  title: string;
  bp?: string;
  lede: string;
  /** Blueprint's rail: its group in Accounting (Setup's for Books setup), and its name there. */
  group: BooksGroup;
  rail: string;
  /** Who opens it by the permissions they hold, beyond who opens Books; everyone who opens Books when absent. */
  may?: (held: readonly string[]) => boolean;
  /** Blueprint's alone (its rail): Riverstone's side list keeps the parts it had. */
  blueprint?: true;
}

// Each part's screen title and its one line (the screen head, as in the accounting prototype).
export const BOOKS_PARTS: readonly BooksPartDef[] = [
  {
    part: 'overview',
    label: 'Overview',
    // A plain home (QA-340): what's there and what needs you, in the owner's words.
    title: 'Your business today',
    bp: 'Where the money stands',
    lede: 'What’s in the bank, who owes you, what you owe, and what needs you.',
    group: 'Money in',
    rail: 'Overview',
  },
  {
    part: 'invoices',
    label: 'Invoices',
    title: 'Invoices',
    lede: 'Every invoice, where it stands, and what is still owed.',
    group: 'Money in',
    rail: 'Invoices',
    blueprint: true,
  },
  {
    part: 'banking',
    label: 'Banking',
    title: 'Banking',
    lede: 'Match what the books already hold, categorise the rest, and reconcile when the difference is zero.',
    group: 'Books',
    rail: 'Banking',
  },
  {
    part: 'receivables',
    label: 'Receivables',
    title: 'Receivables',
    lede: 'Who owes us, how old it is, and what we are doing about it.',
    group: 'Money in',
    rail: 'Money owed',
  },
  {
    part: 'payments',
    label: 'Payments and refunds',
    title: 'Payments and refunds',
    lede: 'Every payment that came in, what it paid, whether it is in the bank, and what went back.',
    group: 'Money in',
    rail: 'Payments and refunds',
    blueprint: true,
  },
  {
    part: 'payables',
    label: 'Payables',
    title: 'Payables',
    lede: 'Bills and receipts to approve, bills to pay, and purchase orders waiting for the owner.',
    group: 'Money out',
    rail: 'Bills to pay',
  },
  {
    part: 'tax',
    label: 'Tax',
    title: 'Tax',
    lede: 'Sales tax returns from the posted lines. File, then record the payment.',
    group: 'Money out',
    rail: 'Sales tax',
  },
  {
    part: 'ledger',
    label: 'Ledger & reports',
    title: 'Ledger & reports',
    lede: 'Reports straight from the journal; every entry shows the lines it posted.',
    group: 'Books',
    rail: 'Ledger and reports',
  },
  {
    part: 'payroll',
    label: 'Payroll',
    title: 'Payroll',
    lede: 'Hours by type from the time entries, ready for the payroll provider.',
    group: 'Books',
    rail: 'Payroll',
    may: seesPayroll,
  },
  {
    part: 'close',
    label: 'Close a month',
    title: 'Close a month',
    lede: 'Check the month, then close it so nothing posts into it. Only the owner reopens one.',
    group: 'Books',
    rail: 'Close a month',
    blueprint: true,
  },
  {
    part: 'approvals',
    label: 'Agent requests',
    title: 'Agent requests',
    lede: 'What the agents asked to do in the books. Nothing runs until a person decides; the input cannot be edited.',
    group: 'Books',
    rail: 'Agent requests',
  },
  {
    part: 'setup',
    label: 'Setup',
    title: 'Books setup',
    lede: 'Four steps, then invoices, payments and bills post on their own.',
    group: 'Setup',
    rail: 'Books setup',
  },
];

/** The parts these permissions open (Payroll only for the owner and the bookkeeper), Blueprint's only in it. */
export const booksPartsFor = (held: readonly string[], blueprint = false): BooksPartDef[] =>
  BOOKS_PARTS.filter((p) => (!p.may || p.may(held)) && (blueprint || !p.blueprint));

export const isBooksPart = (s: string | undefined): s is BooksPart => BOOKS_PARTS.some((p) => p.part === s);
